import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { FlightAlertsHeader } from "./FlightAlertsHeader";
import { FlightAlertsSignInLink } from "./FlightAlertsSignInLink";
import { StepIndicator } from "./StepIndicator";
import { HkiaAttribution } from "./HkiaAttribution";
import { BotCheckStep } from "./BotCheckStep";
import { DirectionStep } from "./DirectionStep";
import { FlightFormStep } from "./FlightFormStep";
import { ResultCard } from "./ResultCard";
import { ConfirmedStep } from "./ConfirmedStep";
import { useFlightLookup } from "./handler/useFlightLookup";
import { useRecaptcha } from "./handler/useRecaptcha";
import { useConfirmWatch } from "./handler/useConfirmWatch";
import { toBackendDirection } from "./handler/mapFlightCardResponse";
import { useReturnContext } from "../SharedLogin/handler/useReturnContext";
import {
  BotCheckState,
  FlightDirection,
  FlightFixture,
  WizardStep,
} from "./types";

// The single destination Flight Alerts registers with Shared Login (DASH-6274). Must match,
// byte for byte, an entry on the backend's SHARED_LOGIN_ALLOWED_DESTINATIONS allowlist — the
// module's allowlist check is exact-match, no normalization.
const SHARED_LOGIN_DESTINATION = "/flight-alerts";

// The one piece of task state that has to survive the round trip to Shared Login and back.
// Shared Login's return context carries only a destination string (see DASH-6274) — nothing
// product-specific — so the selected fixture is stashed here instead, same-origin, and read
// back once Shared Login hands the visitor back.
const PENDING_WATCH_STORAGE_KEY = "flightAlerts.pendingWatch";

// <FlightAlerts> unmounts every time the visitor navigates away (e.g. to the Alert list) and
// remounts fresh on the way back, which would otherwise re-run the bot-check step every single
// time even though the same tab already passed it minutes/seconds earlier. sessionStorage
// survives that unmount/remount without surviving a real new session (new tab, browser
// restart), which is exactly the "already did this in this visit" scope the check is meant to
// cover — a fresh tab still gets asked once, same as before.
const BOT_CHECK_VERIFIED_STORAGE_KEY = "flightAlerts.botCheckVerified";

const hasPassedBotCheckThisSession = (): boolean => {
  try {
    return sessionStorage.getItem(BOT_CHECK_VERIFIED_STORAGE_KEY) === "true";
  } catch {
    // Storage can throw in locked-down browser contexts (private mode, disabled storage) --
    // fail open to "not yet verified" so those visitors just see the check again, same as today.
    return false;
  }
};

interface PendingWatch {
  fixture: FlightFixture;
}

interface HandoverLocationState {
  returnContextToken?: string;
}

const STEP_NUMBER: Partial<Record<WizardStep, number>> = {
  botCheck: 1,
  direction: 2,
  flightForm: 3,
};

interface LastQuery {
  flightNumber: string;
  isoDate: string;
}

const FlightAlerts: React.FC = () => {
  const { t } = useTranslation("flightAlerts");
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const forcedBotCheck = searchParams.get("botCheck");
  const isForcedFailed = forcedBotCheck === "failed";
  const isForcedUnavailable = forcedBotCheck === "unavailable";
  const returnContextToken = (location.state as HandoverLocationState | null)?.returnContextToken;
  // The forced-state query params are an explicit QA hook (DASH-6077) -- they win over an
  // already-passed check rather than being silently skipped by it.
  const alreadyPassedBotCheck =
    !isForcedFailed && !isForcedUnavailable && hasPassedBotCheckThisSession();

  const [wizardStep, setWizardStep] = useState<WizardStep>(
    isForcedUnavailable || alreadyPassedBotCheck ? "direction" : "botCheck",
  );
  const [botCheckState, setBotCheckState] = useState<BotCheckState>(
    isForcedFailed
      ? "failed"
      : isForcedUnavailable
        ? "unavailable"
        : alreadyPassedBotCheck
          ? "checked"
          : "pending",
  );
  const [direction, setDirection] = useState<FlightDirection | null>(null);
  const [lastQuery, setLastQuery] = useState<LastQuery | null>(null);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [selectedFixture, setSelectedFixture] = useState<FlightFixture | null>(
    null,
  );
  const [isBotCheckVerifying, setIsBotCheckVerifying] = useState(false);
  const [isResumingHandover, setIsResumingHandover] = useState(() => Boolean(returnContextToken));
  const [handoverErrorCode, setHandoverErrorCode] = useState<string | undefined>(undefined);
  const { getToken } = useRecaptcha();
  const { data, isLoading, error, lookup, reset } = useFlightLookup(getToken);
  const { confirmWatch } = useConfirmWatch();
  const { handBack } = useReturnContext();
  const hasHandledReturnContext = useRef(false);

  const handleBotCheckChange = (next: BotCheckState) => {
    if (next === "checked") {
      setIsBotCheckVerifying(true);
      getToken("flight_lookup")
        .then(() => {
          try {
            sessionStorage.setItem(BOT_CHECK_VERIFIED_STORAGE_KEY, "true");
          } catch {
            // Best-effort -- worst case this tab is asked to verify again next time.
          }
          setBotCheckState("checked");
          setWizardStep("direction");
        })
        .catch(() => {
          setBotCheckState("unavailable");
          setWizardStep("direction");
        })
        .finally(() => setIsBotCheckVerifying(false));
      return;
    }
    setBotCheckState(next);
    if (next === "unavailable") {
      setWizardStep("direction");
    }
  };

  const handleDirectionSelect = (next: FlightDirection) => {
    setDirection(next);
    setWizardStep("flightForm");
  };

  const handleFlightSubmit = (flightNumber: string, isoDate: string) => {
    if (!direction) return;
    setLastQuery({ flightNumber, isoDate });
    lookup(direction, flightNumber, isoDate);
  };

  const handleChangeDirection = () => {
    reset();
    setDirection(null);
    setSelectedFixture(null);
    setWizardStep("direction");
  };

  // DASH-6274 handover: hand the visitor to Shared Login to prove their phone number, carrying
  // the flight they picked in sessionStorage (same-origin) since the return context itself only
  // carries a destination string, nothing product-specific.
  const handleStartOptIn = (fixture: FlightFixture) => {
    const pending: PendingWatch = { fixture };
    sessionStorage.setItem(PENDING_WATCH_STORAGE_KEY, JSON.stringify(pending));
    navigate(`/shared-login?destination=${encodeURIComponent(SHARED_LOGIN_DESTINATION)}`);
  };

  // Runs once, only when landed on via a Shared Login hand-back (a returnContextToken in router
  // state). Consumes the token for the verified phone number, restores the fixture stashed
  // before the redirect, and confirms the watch — the same call handleVerifyCode used to make
  // directly, now made after the round trip instead of in-page.
  useEffect(() => {
    if (!returnContextToken || hasHandledReturnContext.current) return;
    hasHandledReturnContext.current = true;

    // The token is single-use: clear it from history state immediately so a refresh of this
    // page never attempts to consume it a second time.
    navigate(location.pathname, { replace: true, state: {} });

    const resume = async () => {
      const raw = sessionStorage.getItem(PENDING_WATCH_STORAGE_KEY);
      sessionStorage.removeItem(PENDING_WATCH_STORAGE_KEY);

      const handBackResult = await handBack(returnContextToken);
      if (!handBackResult.success || !handBackResult.phoneNumber) {
        setHandoverErrorCode(handBackResult.errorCode ?? "UNKNOWN");
        setIsResumingHandover(false);
        return;
      }

      let pending: PendingWatch | undefined;
      try {
        pending = raw ? (JSON.parse(raw) as PendingWatch) : undefined;
      } catch {
        pending = undefined;
      }
      if (!pending?.fixture) {
        setHandoverErrorCode("MISSING_PENDING_WATCH");
        setIsResumingHandover(false);
        return;
      }

      setPhoneNumber(handBackResult.phoneNumber);
      setDirection(pending.fixture.direction);
      setSelectedFixture(pending.fixture);

      const watchResult = await confirmWatch({
        phoneNumber: handBackResult.phoneNumber,
        direction: toBackendDirection(pending.fixture.direction),
        operatingDesignator: pending.fixture.flightNumber,
        flightDate: pending.fixture.isoDate,
      });
      if (!watchResult.success) {
        setHandoverErrorCode(watchResult.errorCode ?? "UNKNOWN");
        setIsResumingHandover(false);
        return;
      }

      setWizardStep("confirmed");
      setIsResumingHandover(false);
    };

    resume();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [returnContextToken]);

  const handleRetryFeed = () => {
    if (!direction || !lastQuery) return;
    lookup(direction, lastQuery.flightNumber, lastQuery.isoDate);
  };

  const handleLookupAnother = () => {
    reset();
    setSelectedFixture(null);
    setWizardStep("flightForm");
  };

  useEffect(() => {
    if (data && data.length > 0) {
      setWizardStep("result");
    }
  }, [data]);

  const stepNumber = STEP_NUMBER[wizardStep];

  if (isResumingHandover) {
    return (
      <div className="min-h-screen bg-app flex flex-col items-center px-6 pt-[74px] pb-6 md:pt-20 md:pb-20">
        <div className="w-full max-w-[600px] flex flex-col gap-5 md:gap-6">
          <FlightAlertsHeader />
          <p className="font-lexend text-[16px]/[20px] font-normal tracking-[-0.1px] text-ink-60">
            {t("handover.resuming")}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-app flex flex-col items-center px-6 pt-[74px] pb-6 md:pt-20 md:pb-20">
      <div className="w-full max-w-[600px] flex flex-col gap-5 md:gap-6">
        {/* flex-wrap, not a fixed stack: side-by-side with the logo whenever there's room (most
            widths), dropping to its own line only when the two would otherwise collide at very
            narrow widths -- a fixed stack looked disconnected once there was width to spare. */}
        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
          <FlightAlertsHeader />
          <FlightAlertsSignInLink />
        </div>
        {handoverErrorCode && (
          <p className="font-lexend text-[14px]/[18px] font-normal tracking-normal text-danger-ink">
            {t("handover.errFailed")}
          </p>
        )}
        {stepNumber && (
          <StepIndicator
            current={stepNumber}
            total={3}
            botCheckState={botCheckState}
          />
        )}
        {wizardStep === "botCheck" && (
          <BotCheckStep
            botCheckState={botCheckState}
            onChange={handleBotCheckChange}
            isVerifying={isBotCheckVerifying}
          />
        )}
        {wizardStep === "direction" && (
          <DirectionStep onSelect={handleDirectionSelect} />
        )}
        {wizardStep === "flightForm" && direction && (
          <FlightFormStep
            direction={direction}
            error={error}
            isLoading={isLoading}
            onSubmit={handleFlightSubmit}
            onChangeDirection={handleChangeDirection}
          />
        )}
        {wizardStep === "result" && data && data.length > 0 && (
          <ResultCard
            fixtures={data}
            botCheckState={botCheckState}
            onStartOptIn={handleStartOptIn}
            onRetryFeed={handleRetryFeed}
            onLookupAnother={handleLookupAnother}
          />
        )}
        {wizardStep === "confirmed" && selectedFixture && (
          <ConfirmedStep fixture={selectedFixture} phoneNumber={phoneNumber} />
        )}
        {botCheckState !== "failed" && <HkiaAttribution />}
      </div>
    </div>
  );
};

export default FlightAlerts;